import { isQrCaptureType } from "types";
import { isPhoneTaskStepComplete } from "lib/task-progress";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "components/Form/Button";
import { Layout } from "components/Layout";
import { PhotoSourcePicker } from "components/PhotoSourcePicker";
import { SaveProgressModal } from "components/SaveProgressModal";
import { StepProgressOverview } from "components/StepProgressOverview";
import { PhoneInstructions } from "components/TaskInstructionSlices";
import { StepDetails } from "components/TaskInstructionSlices/StepDetails";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import {
  StepDetailsData,
  useMultiStepCapture,
} from "contexts/MultiStepCaptureContext";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import { useStepNavigation } from "hooks/useStepNavigation";
import { useTaskDefinition } from "hooks/useTaskDefinition";
import { apiGetUserProfile } from "lib/api";
import { getLabelRelations } from "lib/label-relations";
import {
  getStepRouteState,
  getStepSchemaFingerprint,
  getTaskStepDetailFields,
  getTaskSteps,
  TaskStep,
} from "lib/task-steps";
import {
  getAcceptedFileTypes,
  getUploadFileValidationMessage,
} from "lib/upload-files";
import {
  hasChangedTaskPositions,
  getStoredWorkTaskSharedDetails,
  getWorkTaskStepSchemaFingerprint,
  getWorkTaskReviewDestination,
  getWorkTaskStepRoute,
  getWorkTaskStepRouteState,
  isStoredCaptureForWorkTaskStep,
  isWorkTaskStepComplete,
} from "lib/work-task-navigation";
import { ResolvedWorkTaskStep } from "lib/work-tasks";
import { getTaskNavigationSteps } from "lib/task-navigation-qr";
import { toast } from "react-toastify";
import { AppRoutes, CaptureType } from "types";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

function CapturePreview({ file, alt }: { file: File; alt: string }) {
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  if (file.type.startsWith("video/")) {
    return (
      <video
        src={previewUrl}
        className="h-full w-full rounded-lg object-cover"
        controls
      />
    );
  }
  if (file.type.startsWith("image/")) {
    return (
      <img
        src={previewUrl}
        alt={alt}
        className="h-full w-full rounded-lg object-cover"
      />
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg bg-f4f7ff p-3 text-center text-sm text-191c20">
      {file.name}
    </div>
  );
}

export function StepCapture() {
  const [init, setInit] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [progressActionPending, setProgressActionPending] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const validateStepDetailsRef = useRef<
    (() => Array<{ id: string; title: string }>) | null
  >(null);
  const pendingStepDetailsSaveRef = useRef<Promise<void>>(Promise.resolve());
  const editedStepDetailsRef = useRef<StepDetailsData | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const privacyModal = usePrivacyModal();
  const { config } = useContext(CollectionDetailsContext);
  const {
    stepCaptures,
    addStepCapture,
    getStepDetails,
    stepDetails,
    addStepDetails,
    currentTaskId,
    isLoadingSteps,
    setCurrentTaskId,
    clearAllCaptures,
  } = useMultiStepCapture();

  const stepNumber = Number(location.state?.stepNumber);
  const routeStepLabel = location.state?.stepLabel || "";
  const routeStepTitle = location.state?.stepTitle || "";
  const routeNumberOfCaptures = Number(location.state?.numberOfCaptures) || 1;
  const routeTotalSteps = Number(location.state?.totalSteps) || 1;
  const taskId = location.state?.taskId as string | undefined;
  const taskName = location.state?.taskName as string | undefined;
  const captureType = location.state?.captureType as CaptureType | undefined;
  const navigationEntryId = location.state?.navigationEntryId as
    | string
    | undefined;
  const editMode = Boolean(location.state?.editMode);
  const workTaskDocumentId = taskId
    ? config?.tasks[taskId]?.workTaskDocumentId
    : undefined;
  const {
    instructions,
    workTask,
    loading: instructionsLoading,
    error: taskLoadError,
  } = useTaskDefinition({
    taskId,
    workTaskDocumentId,
    enabled: config !== undefined,
    reloadKey: loadAttempt,
  });
  const instructionError = Boolean(taskLoadError);
  const workTaskSharedDetails = useMemo(
    () =>
      workTask ? getStoredWorkTaskSharedDetails(workTask, stepCaptures) : null,
    [stepCaptures, workTask]
  );

  const taskSteps = useMemo(
    () => workTask?.steps || getTaskSteps(instructions),
    [instructions, workTask]
  );
  const navigationSteps = useMemo(() => {
    if (!navigationEntryId || !workTask) return undefined;
    try {
      return getTaskNavigationSteps(workTask, navigationEntryId);
    } catch {
      return [];
    }
  }, [navigationEntryId, workTask]);
  const currentStepIndex = taskSteps.findIndex(
    (step) => step.stepNumber === stepNumber
  );
  const currentStep = taskSteps[currentStepIndex];
  const currentWorkTaskStep = workTask
    ? (currentStep as ResolvedWorkTaskStep | undefined)
    : undefined;
  const effectiveCaptureType = currentWorkTaskStep?.captureType || captureType;
  const totalSteps = taskSteps.length || routeTotalSteps;
  const stepLabel = currentStep?.stepLabel || routeStepLabel;
  const stepTitle = currentStep?.stepTitle || routeStepTitle;
  const numberOfCaptures =
    currentStep?.numberOfCaptures || routeNumberOfCaptures;
  const maximumNumberOfCaptures = currentWorkTaskStep
    ? currentWorkTaskStep.maximumNumberOfCaptures
    : numberOfCaptures;
  const canAddMoreFiles =
    maximumNumberOfCaptures === undefined ||
    files.length < maximumNumberOfCaptures;
  const stepInstructionSlices = useMemo(
    () =>
      currentWorkTaskStep
        ? currentWorkTaskStep.instructionSlices
        : currentStep?.slice
        ? [currentStep.slice]
        : [],
    [currentStep, currentWorkTaskStep]
  );
  const instructionSettingsSlices = currentWorkTaskStep
    ? workTask?.steps.find(
        (step) => step.captureTaskIndex === currentWorkTaskStep.captureTaskIndex
      )?.instructionSlices
    : instructions?.instruction_content;
  const hideInstructions = Boolean(
    instructionSettingsSlices?.some(
      (slice: any) =>
        slice.slice_type === "instruction_text___image" &&
        slice.primary?.hide_instructions === true
    )
  );
  const stepDetailsFields = useMemo(() => {
    if (currentWorkTaskStep) {
      const firstCaptureStep = workTask?.steps.find(
        (step) => step.captureTaskIndex === currentWorkTaskStep.captureTaskIndex
      );
      const captureFields =
        firstCaptureStep?.stepNumber === currentWorkTaskStep.stepNumber
          ? currentWorkTaskStep.collectionDetailFields
          : [];
      const fields = [
        ...captureFields,
        ...currentWorkTaskStep.stepDetailFields,
      ];
      const seen = new Set<string>();
      return fields.filter((field) => {
        const id = field.label_id?.toString().trim() || "";
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    }
    return getTaskStepDetailFields(instructions, stepNumber);
  }, [currentWorkTaskStep, instructions, stepNumber, workTask]);
  const schemaFingerprint = useMemo(
    () =>
      currentWorkTaskStep
        ? getWorkTaskStepSchemaFingerprint(currentWorkTaskStep)
        : getStepSchemaFingerprint({
            captureType: effectiveCaptureType,
            numberOfCaptures,
            fields: stepDetailsFields,
            stepLabel: currentStep?.configuredStepLabel ? stepLabel : undefined,
          }),
    [
      currentWorkTaskStep,
      effectiveCaptureType,
      currentStep,
      stepLabel,
      numberOfCaptures,
      stepDetailsFields,
    ]
  );
  const labelRelations = useMemo(
    () =>
      currentWorkTaskStep && workTask
        ? [...workTask.labelRelations, ...currentWorkTaskStep.labelRelations]
        : getLabelRelations(instructions?.instruction_content || []),
    [currentWorkTaskStep, instructions, workTask]
  );
  const storedStepCapture = stepCaptures.find(
    (capture) => capture.stepNumber === stepNumber
  );
  const existingStepCapture =
    currentWorkTaskStep && storedStepCapture
      ? isStoredCaptureForWorkTaskStep(currentWorkTaskStep, storedStepCapture)
        ? storedStepCapture
        : undefined
      : storedStepCapture;
  const hasStartedUpload = Boolean(
    storedStepCapture?.uploadCaptureId ||
      storedStepCapture?.uploadedFileKeys?.length
  );

  useEffect(() => {
    if (taskId) setCurrentTaskId(taskId);
  }, [setCurrentTaskId, taskId]);

  useEffect(() => {
    setFiles(
      existingStepCapture && existingStepCapture.stepLabel !== stepLabel
        ? []
        : existingStepCapture?.files || []
    );
    validateStepDetailsRef.current = null;
    editedStepDetailsRef.current = null;
  }, [existingStepCapture, stepLabel, stepNumber, taskId]);

  useEffect(() => {
    if (privacyModal.accepted === false || !stepNumber || !taskId) {
      navigate(AppRoutes.home);
    } else {
      setInit(true);
    }
  }, [navigate, privacyModal.accepted, stepNumber, taskId]);

  useEffect(() => {
    if (!hasStartedUpload || currentTaskId !== taskId || isLoadingSteps) return;
    navigate(AppRoutes.multiStepUpload, {
      replace: true,
      state: { taskId, taskName, captureType },
    });
  }, [
    captureType,
    currentTaskId,
    hasStartedUpload,
    isLoadingSteps,
    navigate,
    taskId,
    taskName,
  ]);

  const addFiles = useCallback(
    (selectedFiles: File[]) => {
      const remaining =
        maximumNumberOfCaptures === undefined
          ? selectedFiles.length
          : Math.max(0, maximumNumberOfCaptures - files.length);
      const accepted: File[] = [];
      for (const file of selectedFiles.slice(0, remaining)) {
        const error = getUploadFileValidationMessage(
          file,
          effectiveCaptureType
        );
        if (error) {
          toast.warn(error);
          continue;
        }
        accepted.push(file);
      }
      if (accepted.length > 0) {
        setFiles((previousFiles) => [...previousFiles, ...accepted]);
      }
    },
    [effectiveCaptureType, files.length, maximumNumberOfCaptures]
  );

  const removeFile = useCallback((index: number) => {
    setFiles((previousFiles) =>
      previousFiles.filter((_, fileIndex) => fileIndex !== index)
    );
  }, []);

  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(event.target.files || []);
      if (selectedFiles.length > 0) addFiles(selectedFiles);
      event.target.value = "";
    },
    [addFiles]
  );

  const handleStepDetailsChange = useCallback(
    (
      changedStepNumber: number,
      details: string[],
      fields?: Array<{
        label_id: string;
        label_title: string;
        label_type: string;
        value: string;
      }>
    ) => {
      const nextDetails = {
        stepNumber: changedStepNumber,
        stepDetails: details,
        stepDetailsFields: fields,
        ...(currentWorkTaskStep?.captureEntryId
          ? { captureEntryId: currentWorkTaskStep.captureEntryId }
          : {}),
      };

      // A completed step opened from final review is only updated when the
      // participant explicitly saves it. Leaving the page keeps the stored
      // details unchanged.
      if (editMode) {
        editedStepDetailsRef.current = nextDetails;
        return;
      }

      const nextSave = pendingStepDetailsSaveRef.current
        .catch(() => undefined)
        .then(() => addStepDetails(nextDetails));

      pendingStepDetailsSaveRef.current = nextSave;
      nextSave.catch((error) => {
        console.error("Failed to save step details:", error);
        toast.error("Your step details could not be saved. Please try again.");
      });
    },
    [addStepDetails, currentWorkTaskStep?.captureEntryId, editMode]
  );

  const saveEditedStepDetails = useCallback(async () => {
    const editedDetails = editedStepDetailsRef.current;
    if (!editedDetails) return;

    await addStepDetails(editedDetails);
    if (editedStepDetailsRef.current === editedDetails) {
      editedStepDetailsRef.current = null;
    }
  }, [addStepDetails]);

  const handleValidationRegister = useCallback(
    (validate: () => Array<{ id: string; title: string }>) => {
      validateStepDetailsRef.current = validate;
    },
    []
  );

  const saveCurrentStepDraft = useCallback(async () => {
    // Final-review edits are committed only by Save Step. Navigating away
    // restores the completed step exactly as it was stored.
    if (editMode) return;
    if (files.length === 0 && !existingStepCapture) return;

    await pendingStepDetailsSaveRef.current;
    const wasComplete = existingStepCapture
      ? existingStepCapture.isComplete ??
        existingStepCapture.files.length >= existingStepCapture.numberOfCaptures
      : false;
    const hasMissingRequiredDetails =
      (validateStepDetailsRef.current?.() || []).length > 0;

    await addStepCapture({
      ...(existingStepCapture || {}),
      stepNumber,
      stepLabel,
      stepTitle,
      numberOfCaptures,
      captureTaskId: currentWorkTaskStep?.captureTaskId || taskId,
      ...(currentWorkTaskStep?.captureEntryId
        ? { captureEntryId: currentWorkTaskStep.captureEntryId }
        : {}),
      captureTaskStepNumber:
        currentWorkTaskStep?.captureTaskStepNumber || stepNumber,
      captureType: effectiveCaptureType as CaptureType | undefined,
      files,
      isComplete:
        wasComplete &&
        files.length >= numberOfCaptures &&
        !hasMissingRequiredDetails,
    });
  }, [
    addStepCapture,
    editMode,
    existingStepCapture,
    files,
    currentWorkTaskStep,
    effectiveCaptureType,
    numberOfCaptures,
    stepLabel,
    stepNumber,
    stepTitle,
    taskId,
  ]);

  const isAnyStepCompleted = useCallback(
    (selectedStepNumber: number) => {
      if (!workTask) {
        const selectedStep = taskSteps.find(
          (step) => step.stepNumber === selectedStepNumber
        );
        return selectedStep
          ? isPhoneTaskStepComplete(
              selectedStep,
              instructions,
              captureType,
              stepCaptures
            )
          : false;
      }
      const selectedStep = workTask.steps.find(
        (step) => step.stepNumber === selectedStepNumber
      );
      return selectedStep
        ? isWorkTaskStepComplete(
            selectedStep,
            stepCaptures,
            stepDetails,
            workTaskSharedDetails
          )
        : false;
    },
    [
      captureType,
      instructions,
      stepCaptures,
      stepDetails,
      taskSteps,
      workTask,
      workTaskSharedDetails,
    ]
  );

  const navigateToStep = useCallback(
    (selectedStep: TaskStep) => {
      if (!taskId) return;

      if (workTask) {
        const workTaskStep = selectedStep as ResolvedWorkTaskStep;
        navigate(getWorkTaskStepRoute(workTaskStep), {
          state: getWorkTaskStepRouteState(
            workTask,
            workTaskStep,
            editMode,
            navigationSteps?.some(
              (step) => step.stepNumber === workTaskStep.stepNumber
            )
              ? navigationEntryId
              : undefined
          ),
        });
        return;
      }

      navigate(AppRoutes.stepCapture, {
        state: getStepRouteState(selectedStep, taskSteps.length, {
          taskId,
          taskName,
          captureType,
          editMode,
        }),
      });
    },
    [
      captureType,
      editMode,
      navigate,
      navigationEntryId,
      navigationSteps,
      taskId,
      taskName,
      taskSteps.length,
      workTask,
    ]
  );

  const handleNavigationSaveError = useCallback((error: unknown) => {
    console.error("Failed to save step before switching:", error);
    toast.error("Your current step could not be saved. Please try again.");
  }, []);

  const {
    completedSteps,
    completionPercentage,
    nextIncompleteStep: nextWorkTaskIncompleteStep,
    previousStep,
    selectPreviousStep: handlePreviousStep,
    selectStep: handleSelectStep,
  } = useStepNavigation({
    steps: taskSteps,
    currentStepNumber: stepNumber,
    isStepCompleted: isAnyStepCompleted,
    saveCurrentStepDraft,
    navigateToStep,
    onSaveError: handleNavigationSaveError,
  });

  const nextIncompleteStep = navigationSteps
    ? navigationSteps.find(
        (step) =>
          step.stepNumber !== stepNumber && !isAnyStepCompleted(step.stepNumber)
      )
    : nextWorkTaskIncompleteStep;

  const handleCompleteStep = useCallback(async () => {
    const missingFields = validateStepDetailsRef.current?.() || [];
    if (missingFields.length > 0) {
      document
        .getElementById(`step-detail-${missingFields[0].id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    try {
      await pendingStepDetailsSaveRef.current;
      await saveEditedStepDetails();
      await addStepCapture({
        ...(existingStepCapture || {}),
        stepNumber,
        stepLabel,
        stepTitle,
        numberOfCaptures,
        captureTaskId: currentWorkTaskStep?.captureTaskId || taskId,
        ...(currentWorkTaskStep?.captureEntryId
          ? { captureEntryId: currentWorkTaskStep.captureEntryId }
          : {}),
        captureTaskStepNumber:
          currentWorkTaskStep?.captureTaskStepNumber || stepNumber,
        captureType: effectiveCaptureType as CaptureType | undefined,
        schemaFingerprint,
        files,
        isComplete: true,
      });
    } catch (error) {
      console.error("Failed to save completed step:", error);
      toast.error("This step could not be saved. Please try again.");
      return;
    }

    if (!nextIncompleteStep && navigationEntryId) {
      toast.success(
        "Task position saved. Complete and review the Work Task to upload its captures."
      );
      navigate(AppRoutes.home);
      return;
    }
    if (!nextIncompleteStep) {
      navigate(AppRoutes.multiStepUpload, {
        state: {
          taskId,
          taskName: workTask?.workTaskName || taskName,
          captureType: effectiveCaptureType,
        },
      });
      return;
    }

    setFiles([]);
    navigateToStep(nextIncompleteStep);
  }, [
    addStepCapture,
    currentWorkTaskStep,
    effectiveCaptureType,
    existingStepCapture,
    files,
    navigate,
    navigationEntryId,
    navigateToStep,
    nextIncompleteStep,
    numberOfCaptures,
    saveEditedStepDetails,
    schemaFingerprint,
    stepLabel,
    stepNumber,
    stepTitle,
    taskId,
    taskName,
    workTask,
  ]);

  const handleCancel = useCallback(() => setShowSaveModal(true), []);

  const handleSaveProgress = useCallback(async () => {
    setProgressActionPending(true);
    try {
      await saveCurrentStepDraft();
      setShowSaveModal(false);
      navigate(AppRoutes.home);
    } catch (error) {
      console.error("Failed to save progress before leaving:", error);
      toast.error("Your progress could not be saved. Please try again.");
    } finally {
      setProgressActionPending(false);
    }
  }, [navigate, saveCurrentStepDraft]);

  const handleDiscardProgress = useCallback(async () => {
    setProgressActionPending(true);
    try {
      await clearAllCaptures();
      setShowSaveModal(false);
      navigate(AppRoutes.home);
    } catch (error) {
      console.error("Failed to delete task progress:", error);
      toast.error("Your progress could not be deleted. Please try again.");
    } finally {
      setProgressActionPending(false);
    }
  }, [clearAllCaptures, navigate]);

  const isStepStarted = useCallback(
    (selectedStepNumber: number) =>
      stepCaptures.some(
        (capture) =>
          capture.stepNumber === selectedStepNumber && capture.files.length > 0
      ) || getStepDetails(selectedStepNumber) !== undefined,
    [getStepDetails, stepCaptures]
  );

  const handleBackToReview = useCallback(() => {
    if (workTask) {
      const destination = getWorkTaskReviewDestination(
        workTask,
        isAnyStepCompleted
      );
      navigate(destination.route, { state: destination.state });
      return;
    }

    const firstIncompleteStep = taskSteps.find(
      (step) => !isAnyStepCompleted(step.stepNumber)
    );
    if (firstIncompleteStep && taskId) {
      navigate(AppRoutes.stepCapture, {
        state: getStepRouteState(firstIncompleteStep, taskSteps.length, {
          taskId,
          taskName,
          captureType,
          editMode: true,
        }),
      });
      return;
    }

    navigate(AppRoutes.multiStepUpload, {
      state: { taskId, taskName, captureType },
    });
  }, [
    captureType,
    isAnyStepCompleted,
    navigate,
    taskId,
    taskName,
    taskSteps,
    workTask,
  ]);

  if (!init) return null;

  if (
    instructionsLoading ||
    config === undefined ||
    currentTaskId !== taskId ||
    isLoadingSteps
  ) {
    return (
      <Layout className="space-y-8">
        <div className="flex min-h-[240px] items-center justify-center">
          <p className="text-858494">Loading step...</p>
        </div>
      </Layout>
    );
  }

  if (
    instructionError ||
    (workTask &&
      hasChangedTaskPositions(workTask, [...stepCaptures, ...stepDetails])) ||
    navigationSteps?.length === 0 ||
    !currentStep ||
    isQrCaptureType(currentWorkTaskStep?.captureType)
  ) {
    return (
      <Layout className="space-y-8">
        <div className="space-y-4 text-center">
          <h1 className="text-24 font-semibold">Step unavailable</h1>
          <p className="text-858494">
            We could not load this step. Your saved progress has not changed.
          </p>
          <Button onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            Try Again
          </Button>
          <button
            onClick={handleCancel}
            className="text-sm text-858494 underline hover:text-191c20"
          >
            Back to Homepage
          </button>
        </div>
        <SaveProgressModal
          show={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveProgress}
          onDiscard={handleDiscardProgress}
          busy={progressActionPending}
        />
      </Layout>
    );
  }

  if (hasStartedUpload) {
    return null;
  }

  const captureLabel =
    effectiveCaptureType === "photo"
      ? "photo"
      : effectiveCaptureType === "video"
      ? "video"
      : "file";

  return (
    <Layout
      key={stepNumber}
      className="space-y-8"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          {canAddMoreFiles && effectiveCaptureType === "photo" && isIOS && (
            <label className="cursor-pointer rounded-xl bg-3461fd p-4.5 text-center text-white hover:bg-3461fd/80">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileInput}
              />
              Take Photo or Choose from Gallery
            </label>
          )}

          {canAddMoreFiles && effectiveCaptureType === "photo" && !isIOS && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileInput}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileInput}
              />
              <button
                className="w-full cursor-pointer rounded-xl bg-3461fd p-4.5 text-center text-white hover:bg-3461fd/80"
                onClick={() => setShowPhotoPicker(true)}
              >
                Take Photo or Choose from Gallery
              </button>
            </>
          )}

          {canAddMoreFiles && effectiveCaptureType !== "photo" && (
            <label className="cursor-pointer rounded-xl bg-3461fd p-4.5 text-center text-white hover:bg-3461fd/80">
              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedFileTypes(effectiveCaptureType)}
                multiple={
                  maximumNumberOfCaptures === undefined ||
                  maximumNumberOfCaptures - files.length > 1
                }
                className="hidden"
                onChange={handleFileInput}
              />
              {effectiveCaptureType === "video"
                ? "Choose Video"
                : effectiveCaptureType === "iris"
                ? "Choose Iris Image"
                : effectiveCaptureType === "pcr"
                ? "Choose PCR ZIP"
                : "Choose File"}
            </label>
          )}

          {files.length >= numberOfCaptures && (
            <Button onClick={handleCompleteStep}>
              {editMode
                ? "Save Step"
                : isAnyStepCompleted(stepNumber) && nextIncompleteStep
                ? `Continue to Step ${nextIncompleteStep.stepNumber}`
                : nextIncompleteStep
                ? "Complete & Next"
                : "Complete Step"}
            </Button>
          )}
          <button
            onClick={handleCancel}
            className="text-sm text-858494 underline hover:text-191c20"
          >
            Back to Homepage
          </button>
        </>
      }
    >
      <SaveProgressModal
        show={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveProgress}
        onDiscard={handleDiscardProgress}
        busy={progressActionPending}
      />
      <PhotoSourcePicker
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onCamera={() => {
          setShowPhotoPicker(false);
          fileInputRef.current?.click();
        }}
        onGallery={() => {
          setShowPhotoPicker(false);
          galleryInputRef.current?.click();
        }}
      />

      <div className="space-y-6">
        {editMode ? (
          <button
            onClick={handleBackToReview}
            className="flex items-center gap-2 text-3461fd hover:text-3461fd/80"
          >
            ← Back to final review
          </button>
        ) : (
          previousStep && (
            <button
              onClick={handlePreviousStep}
              className="flex items-center gap-2 text-3461fd hover:text-3461fd/80"
            >
              ← Back to Step {previousStep.stepNumber}
            </button>
          )
        )}

        {totalSteps > 1 && (
          <div className="space-y-2">
            <div className="h-2 w-full rounded-full bg-f4f7ff">
              <div
                className="h-2 rounded-full bg-3461fd transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-858494">
              <span>
                {completedSteps} of {totalSteps} complete
              </span>
              <span>{completionPercentage}% complete</span>
            </div>
          </div>
        )}

        <StepProgressOverview
          steps={taskSteps}
          currentStepNumber={stepNumber}
          isStepCompleted={isAnyStepCompleted}
          isStepStarted={isStepStarted}
          onSelectStep={handleSelectStep}
        />

        <section aria-label={`Step ${stepNumber} instructions`}>
          <PhoneInstructions
            slices={stepInstructionSlices}
            taskTitle={
              instructionSettingsSlices?.find(
                (slice: any) => slice.slice_type === "instruction_text___image"
              )?.primary?.title1
            }
            stepTitle={stepTitle}
            numberOfCaptures={numberOfCaptures}
            hideInstructions={hideInstructions}
          />
        </section>

        {files.length > 0 && (
          <section className="space-y-4" aria-label="Step captures">
            <div className="text-center">
              <h2 className="text-24 font-semibold">
                Review {captureLabel}
                {numberOfCaptures > 1 ? "s" : ""}
              </h2>
              {numberOfCaptures > 1 && (
                <p className="mt-2 text-sm text-3461fd">
                  {files.length} of {numberOfCaptures} added
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${file.lastModified}-${index}`}
                  className="relative aspect-square"
                >
                  <CapturePreview
                    file={file}
                    alt={`Step ${stepNumber} capture ${index + 1}`}
                  />
                  <button
                    onClick={() => removeFile(index)}
                    aria-label={`Remove capture ${index + 1}`}
                    className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-sm text-white hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {(stepDetailsFields.length > 0 ||
              currentStep?.stepLabels?.length) && (
              <StepDetails
                stepNumber={stepNumber}
                stepDetailsFields={stepDetailsFields}
                onValidationRegister={handleValidationRegister}
                onStepDetailsChange={handleStepDetailsChange}
                getStepDetails={getStepDetails}
                taskId={taskId}
                loadUserProfile={apiGetUserProfile}
                labelRelations={labelRelations}
                stepLabels={currentStep?.stepLabels}
              />
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}
